/**
 * Recently typed values for a field family ("motor-hp", "length-ft", …),
 * persisted per browser so the last few distinct entries come back as
 * one-tap chips under the input. Newest first, at most `max` entries.
 */

import { useCallback, useState } from 'react';

const STORAGE_PREFIX = 'bgd-recent-values:';
export const RECENT_VALUES_MAX = 5;

export function readRecentValues(key: string): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is number => typeof v === 'number' && Number.isFinite(v)) : [];
  } catch {
    return [];
  }
}

export function pushRecentValue(key: string, value: number, max = RECENT_VALUES_MAX): number[] {
  if (!Number.isFinite(value)) return readRecentValues(key);
  const next = [value, ...readRecentValues(key).filter((v) => v !== value)].slice(0, max);
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(next));
  } catch {
    /* private mode / quota — chips simply don't persist */
  }
  return next;
}

/**
 * [values, remember]. With no key the hook is inert (empty list, no-op).
 */
export function useRecentValues(key: string | undefined, max = RECENT_VALUES_MAX): [number[], (v: number) => void] {
  const [values, setValues] = useState<number[]>(() => (key ? readRecentValues(key) : []));
  const remember = useCallback(
    (v: number) => {
      if (!key) return;
      setValues(pushRecentValue(key, v, max));
    },
    [key, max]
  );
  return [key ? values : [], remember];
}
